import { useState } from 'react';
import toast from 'react-hot-toast';
import { Store, Check } from 'lucide-react';

import { useAuth } from '../auth/AuthProvider.jsx';
import { ApiError } from '../api/client.js';
import { Card } from '../components/ui/Card.jsx';
import { Button } from '../components/ui/Button.jsx';
import { env } from '../lib/env.js';

/**
 * Store picker shown after login to scoped users assigned to more than one
 * store. The choice pins the session to a single store so store specific
 * features (AI assistant, knowledge base) know which one to use.
 */
export function StoreSelectPage() {
  const { storeSelection, selectStore, logout } = useAuth();
  const stores = (storeSelection && storeSelection.stores) || [];
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await selectStore(selected);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Impossibile selezionare il negozio.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface px-4">
      <Card className="w-full max-w-md">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{env.appName}</p>
          <h1 className="mt-1 text-2xl font-semibold text-ink">Seleziona il negozio</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Sei assegnato a piu negozi. Scegli quello su cui vuoi operare in questa sessione.
          </p>
        </header>

        <ul className="flex flex-col gap-2">
          {stores.map((s) => {
            const active = selected === s._id;
            return (
              <li key={s._id}>
                <button
                  type="button"
                  onClick={() => setSelected(s._id)}
                  className={[
                    'flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors',
                    active
                      ? 'border-ink bg-surface-subtle'
                      : 'border-border hover:border-border-strong hover:bg-surface-subtle'
                  ].join(' ')}
                >
                  <span className={[
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-md',
                    active ? 'bg-ink text-surface-elevated' : 'bg-surface-subtle text-ink-muted'
                  ].join(' ')}>
                    <Store className="h-4 w-4" strokeWidth={1.75} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-ink">{s.name}</span>
                    {s.code && <span className="block truncate text-xs text-ink-muted">{s.code}</span>}
                  </span>
                  {active && <Check className="h-4 w-4 shrink-0 text-ink" strokeWidth={2.25} />}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="mt-6 flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={logout} disabled={submitting}>Esci</Button>
          <Button onClick={confirm} loading={submitting} disabled={!selected}>Continua</Button>
        </div>
      </Card>
    </div>
  );
}
